import React, { useState, useEffect } from 'react';
import propTypes from 'prop-types';
import AlertModal from '../../../AlertModal';
import PageLoader from '../../../PageLoader';
import SearchForm from '../SearchForm';
import SelectedUser from './components/SelectedUser';
import { compareByForeman } from 'helpers/_helpers';
import { isHoursOverlapseTimeOffs } from '../../helpers';
import { useSelector } from 'react-redux';
import { useCanEditWorklogCrewAndTime, useCanEditWorklogCrewLeader } from 'common/hooks/use-can-edit-worklog';
import { useTimeSettingsFormat } from 'common/useTimeSettingsFormat';
import { convertTimeObject, convertTo24HourFormat } from 'common/dateFormatConfig';
import { getEffectiveStartTime } from 'helpers/helpers';
import { add, differenceInDays, isAfter, isSameMinute, parse, set } from 'date-fns';


const PeopleBlock = ({
  sheet,
  usedItems,
  setSubmitData,
  unavailableResources,
  list,
  listOfAll,
  isSubmitting,
  setResourceItemsErrorsAtIndex,
}) => {
  const sheetsTimeoffs = useSelector((store) => store.currentWorklog.worklogSheet.timeOffs);
  const getTimeFormatHook = useTimeSettingsFormat();
  const is12Format = getTimeFormatHook().is12Format;
  const canEditStartTime = useCanEditWorklogCrewAndTime(sheet);
  const canEditCrewLeader = useCanEditWorklogCrewLeader(sheet);

  const [selectedResources, setSelectedResources] = useState([]);
  const [availableResources, setAvailableResources] = useState([]);

  const [infoModal, setInfoModal] = useState({
    isOpen: false,
    text: '',
  });

  const hoursAdapter = (hourObj, startTimeDate) => {
    const {
      hour: hourStart,
      minute: minuteStart,
      amPm: amPmStart,
    } = convertTimeObject(getEffectiveStartTime(hourObj, startTimeDate), is12Format);

    const dayStart = startTimeDate || sheet.startTimeDate
      ? differenceInDays(new Date(startTimeDate || sheet.startTimeDate), new Date(sheet.createdAt))
      : 0;

    return {
      dayStart: `${dayStart}`,
      hourStart,
      minuteStart,
      amPmStart,
    };
  };

  useEffect(() => {
    const workers = sheet.workers.map((worker) => {
      return {
        ...worker,
        ...(worker.startTime
          ? {
              startTime: hoursAdapter(worker.startTime, worker.startTimeDate),
            }
          : {}),
      };
    });

    if (workers.length && sheetsTimeoffs) {
      const usedResourcesIds = workers.map((w) => w._id);
      const availableResourcesData = list.filter((el) => !usedResourcesIds.includes(el._id));
      const workersWithTimeoffs = [];
      workers.forEach((w, i, arr) => {
        const userTimeOffs = sheetsTimeoffs.filter((el) => el.userId === w._id);
        const res = userTimeOffs.map((timeOff) => ({
          start: new Date(timeOff.utcStartDate),
          end: new Date(timeOff.utcEndDate),
        }));

        workersWithTimeoffs.push({ ...w, availableHours: res });
        if (i + 1 === arr.length) {
          setAvailableResources(availableResourcesData);
          setSelectedResources(workersWithTimeoffs);
        }
      });
    } else {
      setAvailableResources([...list]);
      setSelectedResources([]);
    }
  }, [list, sheetsTimeoffs, sheet]);

  useEffect(() => {
    const isValidTimePeriods = !selectedResources.some((r) => r.error);
    const isValidTravelTime = !selectedResources.some((r) => r.travelTimeErrors);
    const isValidHours = selectedResources.reduce((isValid, r) => (
      !r.hours?.some((h) => h.error) && isValid
    ), true);

    const rawData = selectedResources.map((r) => {
      const resource = { ...r };
      if (resource.hours) {
        const rawHours = resource.hours.map((h) => {
          const hour = {
            ...h,
            travelTimeTo: {
              hours: h.travelTimeTo?.hours || '0',
              minutes: h.travelTimeTo?.minutes || '0',
            },
            travelTimeFrom: {
              hours: h.travelTimeFrom?.hours || '0',
              minutes: h.travelTimeFrom?.minutes || '0',
            },
          };
          if (sheet.travelTimeOnly) {
            delete hour.start;
            delete hour.end;
          }
          delete hour.error;
          return hour;
        });
        const resultData = sheet.travelTimeOnly
          ? rawHours.map((el, i) =>
              i === 0 ? el : { ...el, isWlTravelTime: false, isWlHours: false }
            )
          : rawHours;

        resource.hours = resultData;
      }
      const startTimeHour = is12Format
        ? convertTo24HourFormat(resource.startTime?.hourStart, resource.startTime?.amPmStart)
        : resource.startTime?.hourStart;
      resource.startTimeDate = set(
        add(new Date(sheet.createdAt), { days: +(resource.startTime?.dayStart) || 0 }),
        { hours: startTimeHour, minutes: resource.startTime?.minuteStart },
      )
      resource.startTime = resource.startTime
        ? {
            hour: is12Format
              ? convertTo24HourFormat(resource.startTime?.hourStart, resource.startTime?.amPmStart)
              : resource.startTime?.hourStart,
            minute: resource.startTime?.minuteStart,
          }
        : null;
      delete resource.availableHours;
      if (resource.error) {
        delete resource.error;
      }
      return resource;
    });
    const isValid =
      isValidTimePeriods && (isValidHours || sheet.travelTimeOnly) && isValidTravelTime;
    if (isSubmitting) {
      setSubmitData(rawData, isValid);
    }
  }, [isSubmitting]);

  const formatList = () => {
    return availableResources.map((user) => ({
      label: user.username,
      id: user._id,
      info: user,
    }));
  };

  const selectItem = (selectedId) => {
    let selectedItem;
    const availableResourcesData = availableResources.filter((el) => {
      if (el._id === selectedId) {
        selectedItem = {
          _id: el._id,
          foreman: false,
        };
      }
      return el._id !== selectedId;
    });

    const userTimeOffs = sheetsTimeoffs?.filter((el) => el.userId === selectedId);
    const res = userTimeOffs?.map((timeOff) => ({
      start: new Date(timeOff.utcStartDate),
      end: new Date(timeOff.utcEndDate),
    }));

    let error = '';
    const hours = { start: new Date(sheet.hours.start), end: new Date(sheet.hours.end) };
    const isInvalidHoursByTimeoffs = isHoursOverlapseTimeOffs([hours], res || [], sheet);

    if (isInvalidHoursByTimeoffs) error = 'User has timeoff at this time';

    // const item = { ...selectedItem };
    const item = { ...selectedItem, availableHours: res, error };

    setAvailableResources(availableResourcesData);
    setSelectedResources((prev) => [...prev, item]);
  };

  const getUser = (user) =>
    listOfAll.find((el) => el._id === user._id) || { ...user, profile: { shifts: user.shifts } };


  const checkAndModifyErrorIsStartTimeEarlier = (resourceData) => {
    if (resourceData?.hours?.length && resourceData?.startTime) {
      let error = resourceData.error || '';
      let startTimeError = false;

      let startTimeDate = resourceData.startTimeDate;
      if (!resourceData.startTimeDate) {
        const startDate = !!(sheet.createdAt && resourceData?.startTime?.dayStart)
          ? add(sheet.createdAt, { days: +(resourceData.startTime?.dayStart ?? '0') })
          : null;
        const earliestDate = resourceData.hours.reduce((earliest, hours) => {
          if (earliest?.start?.getTime() < hours?.start?.getTime()) {
            return earliest;
          }
          return hours;
        });
  
        const startTimeObj = [startDate || earliestDate.start, resourceData.startTime.hourStart, resourceData.startTime.minuteStart, resourceData.startTime.amPmStart];
        startTimeDate = parse(
          getTimeFormatHook(startTimeObj).formattedTime,
          getTimeFormatHook(startTimeObj).formatForTimePars,
          new Date(startDate || earliestDate.start) || new Date() // new Date is just a fallback
        );
      }

      const startHoursErrors = resourceData.hours.map((hours) => {
        if (isSameMinute(startTimeDate, hours.start) || isAfter(startTimeDate, hours.start)) {
          startTimeError = 'On Job Time should be later than the Start Time';
          return true;
        }
        return null;
      });

      if (startTimeError) {
        if (!error.includes(startTimeError)) {
          error += error
            ? `, ${startTimeError}`
            : startTimeError;
          resourceData.error = error;
        }

        resourceData.errorData = {
          startTimeError: true,
          startHoursErrors,
        }
      } else {
        error = error.replace(/(, )?On Job Time should be later than the Start Time/, '');
        resourceData.error = error;

        delete resourceData.errorData;
      }
    }
  }

  const updateSelectedResource = (id, data) => {
    const resourceData = structuredClone(data);
    checkAndModifyErrorIsStartTimeEarlier(resourceData);

    const resourceIndex = selectedResources.findIndex((el) => el._id === id);
    const resources = [...selectedResources];
    resources[resourceIndex] = resourceData;

    setResourceItemsErrorsAtIndex(resourceIndex, !!resourceData?.error);

    setSelectedResources(resources.sort(compareByForeman));
  };

  const removeSelectedResource = (id) => {
    const selectedResourcesData = selectedResources.filter((el) => el._id !== id);
    const usedResourcesIds = selectedResourcesData.map((w) => w._id);
    const availableResources = list.filter((el) => !usedResourcesIds.includes(el._id));

    setAvailableResources(availableResources);
    setSelectedResources(selectedResourcesData);
  };

  const closeInfoModal = () => setInfoModal({ isOpen: false, text: '' });

  return (
    <PageLoader loading={false}>
      <>
        <div>
          <SearchForm
            list={formatList()}
            selectItem={selectItem}
            type="people"
            usedItems={usedItems}
            unavailableResources={unavailableResources}
          />
        </div>

        <div>
          {selectedResources.map((r) => (
            <SelectedUser
              key={r._id}
              user={getUser(r)}
              sheet={sheet}
              workerData={r}
              updateSelectedResource={updateSelectedResource}
              removeSelectedResource={removeSelectedResource}
              canEditStartTime={canEditStartTime}
              canEditCrewLeader={canEditCrewLeader(r)}
            />
          ))}
        </div>

        {infoModal.isOpen && (
          <AlertModal isOpen={infoModal.isOpen} info={infoModal.text} onClose={closeInfoModal} />
        )}
      </>
    </PageLoader>
  );
};

PeopleBlock.propTypes = {
  list: propTypes.array.isRequired,
  sheet: propTypes.object.isRequired,
  isSubmitting: propTypes.bool.isRequired,
  setSubmitData: propTypes.func.isRequired,
  usedItems: propTypes.array.isRequired,
  unavailableResources: propTypes.array.isRequired,
  setResourceItemsErrorsAtIndex: propTypes.func.isRequired,
};

export default PeopleBlock;
